export interface LegalSection {
  heading: string
  body: string[]
}

export interface LegalDocument {
  title: string
  updated: string
  sections: LegalSection[]
}

const SUPPORT_EMAIL = 'nutrinaturalonline@gmail.com'

export const privacyPolicy: LegalDocument = {
  title: 'Política de Privacidad',
  updated: 'Última actualización: 1 de enero de 2026',
  sections: [
    {
      heading: '1. Información general',
      body: [
        'Esta Política de Privacidad describe cómo se recopila, utiliza, almacena y protege la información personal de las personas que visitan este sitio web o adquieren el producto digital “Código Cortisol” (en adelante, “el Producto”).',
        `Al utilizar este sitio o realizar una compra, aceptas las prácticas descritas en este documento. Si no estás de acuerdo con ellas, te pedimos que no utilices el sitio. Para cualquier consulta relacionada con privacidad puedes escribirnos a ${SUPPORT_EMAIL}.`,
      ],
    },
    {
      heading: '2. Datos que recopilamos',
      body: [
        'Datos que tú nos proporcionas: nombre y dirección de correo electrónico al completar un formulario de compra, suscripción o solicitud de soporte.',
        'Datos de pago: las transacciones son procesadas íntegramente por pasarelas de pago externas. No almacenamos ni tenemos acceso a los números completos de tarjetas de crédito o débito.',
        'Datos técnicos y de uso: dirección IP, tipo de dispositivo, navegador, sistema operativo, páginas visitadas, tiempo de permanencia y origen del tráfico, recopilados mediante cookies y tecnologías similares.',
      ],
    },
    {
      heading: '3. Finalidad del tratamiento',
      body: [
        'Utilizamos la información para: entregar el acceso al Producto adquirido; gestionar pagos, facturación y solicitudes de reembolso; brindar soporte al cliente; enviar comunicaciones relacionadas con el Producto y, si lo has consentido, contenidos educativos y promocionales; medir y mejorar el rendimiento del sitio y de nuestras campañas publicitarias; y cumplir obligaciones legales.',
      ],
    },
    {
      heading: '4. Cookies y tecnologías de medición',
      body: [
        'Este sitio utiliza cookies propias y de terceros. Las cookies necesarias permiten el funcionamiento básico del sitio y del proceso de compra. Las cookies analíticas y publicitarias nos permiten entender el comportamiento agregado de los visitantes y medir la efectividad de nuestros anuncios.',
        'Podemos utilizar herramientas de medición y publicidad de terceros, como Meta Pixel (Facebook e Instagram), Google Analytics y Google Ads, que pueden instalar cookies y recibir datos de eventos del sitio (por ejemplo, visualización de página o compra realizada). Estos datos se tratan conforme a las políticas de privacidad de dichos proveedores.',
        'Puedes bloquear o eliminar las cookies desde la configuración de tu navegador. Ten en cuenta que algunas funciones del sitio podrían no operar correctamente si las desactivas.',
      ],
    },
    {
      heading: '5. Publicidad basada en intereses',
      body: [
        'Podemos mostrar anuncios en plataformas de terceros a audiencias definidas por intereses o por interacción previa con nuestro sitio (remarketing). No utilizamos categorías de datos sensibles para segmentar publicidad. Puedes gestionar tus preferencias publicitarias directamente en la configuración de anuncios de cada plataforma.',
      ],
    },
    {
      heading: '6. Con quién compartimos la información',
      body: [
        'Compartimos datos únicamente con proveedores que nos permiten operar: pasarelas de pago y plataformas de venta de productos digitales, plataformas de entrega y alojamiento de contenido, proveedores de email marketing y herramientas de analítica y publicidad.',
        'No vendemos ni alquilamos tus datos personales a terceros. Podremos divulgar información cuando sea requerido por una autoridad competente o para proteger derechos legítimos.',
      ],
    },
    {
      heading: '7. Conservación y seguridad',
      body: [
        'Conservamos los datos personales durante el tiempo necesario para cumplir las finalidades descritas y las obligaciones legales, contables y fiscales aplicables. Aplicamos medidas técnicas y organizativas razonables para proteger la información contra el acceso, la alteración o la divulgación no autorizados. Ningún sistema es completamente infalible, por lo que no podemos garantizar una seguridad absoluta.',
      ],
    },
    {
      heading: '8. Tus derechos',
      body: [
        `Puedes solicitar en cualquier momento el acceso, la rectificación, la portabilidad, la limitación o la eliminación de tus datos personales, así como retirar tu consentimiento para el envío de comunicaciones comerciales. Para ejercer estos derechos escribe a ${SUPPORT_EMAIL} indicando tu solicitud y el correo electrónico utilizado en la compra. Responderemos en un plazo razonable conforme a la normativa aplicable.`,
        'Todos los correos comerciales incluyen un enlace de baja que puedes usar para dejar de recibirlos de inmediato.',
      ],
    },
    {
      heading: '9. Menores de edad',
      body: [
        'Este sitio y el Producto están dirigidos exclusivamente a personas mayores de 18 años. No recopilamos de forma intencionada información de menores de edad. Si detectamos que hemos recibido datos de un menor, procederemos a eliminarlos.',
      ],
    },
    {
      heading: '10. Cambios en esta política',
      body: [
        'Podemos actualizar esta Política de Privacidad para reflejar cambios legales u operativos. La versión vigente será siempre la publicada en esta página, con su fecha de actualización correspondiente.',
      ],
    },
    {
      heading: '11. Contacto',
      body: [
        `Para consultas sobre privacidad, datos personales o soporte: ${SUPPORT_EMAIL}.`,
      ],
    },
  ],
}

export const termsOfService: LegalDocument = {
  title: 'Términos y Condiciones',
  updated: 'Última actualización: 1 de enero de 2026',
  sections: [
    {
      heading: '1. Aceptación de los términos',
      body: [
        'Al acceder a este sitio web o adquirir el producto digital “Código Cortisol” (en adelante, “el Producto”), declaras haber leído y aceptado íntegramente estos Términos y Condiciones. Si no estás de acuerdo con alguna de sus cláusulas, no debes realizar la compra.',
      ],
    },
    {
      heading: '2. Naturaleza del Producto',
      body: [
        'Código Cortisol es un producto digital de carácter educativo e informativo, entregado en formato descargable y de acceso inmediato tras la confirmación del pago. No incluye consultas personalizadas, seguimiento individual, suplementos, alimentos ni productos físicos.',
        'El Producto no constituye un servicio médico, nutricional, psicológico ni terapéutico, y no establece una relación profesional-paciente de ningún tipo.',
      ],
    },
    {
      heading: '3. Aviso de salud',
      body: [
        'El contenido no reemplaza el diagnóstico, el tratamiento ni el consejo de un profesional de la salud cualificado. Si estás embarazada, en período de lactancia, tomas medicación, tienes una condición metabólica, endocrina, cardiovascular, renal o cualquier otra condición médica, consulta a tu médico antes de aplicar cambios en tu alimentación, tu actividad física o tus hábitos.',
        'Nunca suspendas ni modifiques un tratamiento médico basándote en la información de este Producto.',
      ],
    },
    {
      heading: '4. Precio, pago y moneda',
      body: [
        'El Producto se ofrece mediante un pago único de USD 15,90 en la oferta promocional vigente (precio regular USD 99). No existen suscripciones, renovaciones automáticas ni cargos recurrentes. Los precios se muestran en dólares estadounidenses (USD) y la pasarela de pago realiza la conversión a tu moneda local antes de confirmar la compra; el importe final puede variar según el tipo de cambio y las comisiones de tu entidad bancaria.',
        'El procesamiento del pago se realiza a través de plataformas externas de venta de productos digitales, sujetas a sus propios términos de servicio.',
      ],
    },
    {
      heading: '5. Entrega y acceso',
      body: [
        'Tras la aprobación del pago recibirás, en el correo electrónico indicado durante la compra, las instrucciones de acceso y descarga del Producto. Es tu responsabilidad proporcionar una dirección de correo válida y revisar las carpetas de spam o promociones. Si no recibes el acceso, escríbenos y lo resolveremos.',
      ],
    },
    {
      heading: '6. Garantía y reembolsos (60 días)',
      body: [
        'Ofrecemos una garantía de satisfacción de 60 días naturales contados desde la fecha de compra. Si dentro de ese plazo consideras que el Producto no cumple con lo prometido, puedes solicitar el reembolso íntegro escribiendo a nuestro correo de soporte desde la dirección utilizada en la compra, sin necesidad de justificar tu decisión.',
        'El reembolso se procesa por el mismo medio de pago utilizado, en los plazos que establezca la plataforma de pago correspondiente. Transcurridos los 60 días, la garantía deja de estar disponible.',
      ],
    },
    {
      heading: '7. Licencia de uso y propiedad intelectual',
      body: [
        'La compra otorga una licencia personal, limitada, no exclusiva y no transferible para el uso individual del Producto. Todos los textos, imágenes, protocolos, marcas y materiales son propiedad de sus titulares y están protegidos por la legislación de propiedad intelectual.',
        'Queda prohibida la reproducción total o parcial, la distribución, la reventa, la cesión, la publicación, la traducción o la creación de obras derivadas sin autorización previa y por escrito. El incumplimiento podrá dar lugar a la cancelación del acceso y a las acciones legales que correspondan.',
      ],
    },
    {
      heading: '8. Resultados y testimonios',
      body: [
        'Los testimonios publicados reflejan experiencias individuales y no constituyen una garantía, promesa ni proyección de resultados. Los resultados varían en función de la genética, el estado de salud, los hábitos, la constancia y el contexto de cada persona. No garantizamos resultados específicos de pérdida de peso, reducción de inflamación, composición corporal ni bienestar.',
      ],
    },
    {
      heading: '9. Limitación de responsabilidad',
      body: [
        'El uso del Producto y la aplicación de su contenido son responsabilidad exclusiva del usuario. En la máxima medida permitida por la ley aplicable, no asumimos responsabilidad por daños directos, indirectos, incidentales o consecuentes derivados del uso o de la imposibilidad de uso del Producto o del sitio web.',
      ],
    },
    {
      heading: '10. Uso del sitio',
      body: [
        'Te comprometes a utilizar este sitio de forma lícita, a no intentar acceder a áreas restringidas, a no interferir en su funcionamiento y a no emplear sistemas automatizados para extraer su contenido. El sitio puede sufrir interrupciones por mantenimiento o causas técnicas ajenas a nuestro control.',
      ],
    },
    {
      heading: '11. Independencia de plataformas de terceros',
      body: [
        'Este sitio no forma parte de Facebook, Instagram ni de Meta Platforms, Inc., ni está patrocinado, avalado o administrado por ellos. FACEBOOK™ e INSTAGRAM™ son marcas registradas de Meta Platforms, Inc. Lo mismo aplica a Google, YouTube y TikTok, cuyas marcas pertenecen a sus respectivos titulares.',
      ],
    },
    {
      heading: '12. Modificaciones y ley aplicable',
      body: [
        'Podemos modificar estos Términos y Condiciones en cualquier momento. La versión aplicable a tu compra será la publicada en el momento de realizarla. Cualquier controversia se resolverá conforme a la legislación vigente aplicable al titular del Producto.',
      ],
    },
    {
      heading: '13. Soporte',
      body: [
        `Para dudas, problemas de acceso o solicitudes de reembolso, escribe a ${SUPPORT_EMAIL}.`,
      ],
    },
  ],
}
